import type { Input, Output } from "../../../../../../api/call";
import type { BlockDiagramModelT } from "../../../schema/block/diagram";
import type { BlockHeadingModelT } from "../../../schema/block/heading";
import type { BlockImageModelT } from "../../../schema/block/image";
import type { BlockPlotModelT } from "../../../schema/block/plot";
import type { BlockTextModelT } from "../../../schema/block/text";
import { omit as omitObject } from "diagnostics-lib/object";
import type { EncodingT } from "../../../schema/encoding";
import type { BlockCommentModelT } from "../../../schema/block/comment";
import { type BlockPresetModelT } from "../../../schema/block/preset";

type BlockTypes = "texts" | "headings" | "images" | "plots" | "diagrams" | "comments" | "presets";

type BlockPayloadMap = Input<"POST", "/projects/:projectId(uuid)/dashboards">["body"]["block"];
type BlockResponseMap = Output<"POST", "/projects/:projectId(uuid)/dashboards">["body"];

type BlockPayload<T extends BlockTypes> = T extends keyof BlockPayloadMap
  ? BlockPayloadMap[T] extends (infer U)[]
    ? U
    : never
  : never;
type BlockResponse<T extends BlockTypes> = T extends keyof BlockResponseMap
  ? BlockResponseMap[T] extends (infer U)[]
    ? U
    : never
  : never;

export function mapPayloadFromBlockText(block: BlockTextModelT): BlockPayload<"texts"> {
  return {
    content: block.data,
    positions: block.position,
  };
}

export function mapPayloadFromBlockComment(
  block: BlockCommentModelT,
): BlockPayload<"comments"> & { id: string } {
  return { id: block.id, positions: block.position };
}

export function mapPayloadFromBlockPreset(
  block: BlockPresetModelT,
): BlockPayload<"presets"> & { id: string } {
  if (block.data.preset === "PRIORITY_FAULT_EVENTS") {
    return {
      id: block.id,
      positions: block.position,
      title: block.data.title,
      preset: block.data.preset,
      systemIds: block.data.systemIds,
    };
  }

  return {
    id: block.id,
    positions: block.position,
    title: block.data.title,
    preset: block.data.preset,
  };
}

export function mapPayloadFromBlockHeading(block: BlockHeadingModelT): BlockPayload<"headings"> {
  return {
    content: block.data,
    positions: block.position,
    level: block.config.level,
  };
}

export function mapPayloadFromBlockImage(block: BlockImageModelT): BlockPayload<"images"> {
  return {
    title: block.data,
    positions: block.position,
    path: block.config.url,
  };
}

export function mapPayloadFromBlockPlot(block: BlockPlotModelT): BlockPayload<"plots"> {
  return {
    positions: block.position,
    // resources: block.data.resources?.map(r =>
    //   r.kind === "FAULT" ? { ...r, kind: "FAILURE", quantity: r.quantity ?? undefined } : r,
    // ),
    transforms: block.data.transforms?.map(t => ({
      aggregates: t.aggregate ?? [],
      filters: t.filter?.map(f => ({ ...f, value: f.value.map(v => `${v}`) })) ?? [],
      groupBy: t.groupBy ?? [],
    })),
    period: block.data.period,
    options: block.config.options,
    title: block.data.title,
    layers: block.config.layers
      .filter(({ mark }) => mark === "line" || mark === "scatter")
      .map(layer => ({
        ...layer,
        position: layer.position?.toUpperCase() as "RIGHT" | "LEFT",
        mark: layer.mark === "line" ? "LINE" : "SCATTER",
      })),
  };
}

export function mapPayloadFromBlockDiagram(block: BlockDiagramModelT): BlockPayload<"diagrams"> {
  return {
    period: block.data.period,
    title: block.data.title,
    transforms: block.data.transforms?.map(t => ({
      aggregates: t.aggregate ?? [],
      filters: t.filter?.map(f => ({ ...f, value: f.value.map(v => `${v}`) })) ?? [],
      groupBy: t.groupBy ?? [],
    })),
    url: block.config.url,
    live: block.config.live ?? false,
    showAll: block.config.show_all ?? true,
    showRole: block.config.show_role ?? false,
    viewport: block.config.viewport ? { ...block.config.viewport } : null,
    positions: block.position as [number, number, number, number],
    aggregate: block.config.aggregate ?? false,
    layers: block.config.layers.map(layer => ({
      name: layer.name ?? undefined,
      values: layer.encodings.map(encoding => ({
        identifier: encoding.identifier,
        column: encoding.column ?? undefined,
        quantity: encoding.quantity ?? undefined,
      })),
      thresholds: layer.thresholds,
      evolutions: layer.evolutions,
      anchorRelativePositions: layer.anchorRelativePositions,
      anchor: {
        x: layer.anchor.x.toUpperCase() as "RIGHT" | "LEFT" | "CENTER",
        y: layer.anchor.y.toUpperCase() as "TOP" | "BOTTOM" | "CENTER",
      },
      positions: layer.position as [number, number, number, number],
      showLabel: layer.showLabel,
      showDescription: layer.showDescription,
    })),
  };
}

export function mapPayloadToBlockText(payload: BlockResponse<"texts">): BlockTextModelT {
  return {
    ...omitObject(payload, ["createdAt", "updatedAt", "userName", "content", "positions"]),
    kind: "TEXT" as BlockTextModelT["kind"],
    timestamp: new Date(payload.updatedAt).getTime(),
    data: payload.content,
    position: payload.positions as [number, number, number, number],
  };
}

export function mapPayloadToBlockComment(payload: BlockResponse<"comments">): BlockCommentModelT {
  return {
    ...omitObject(payload, ["createdAt", "updatedAt", "userName", "positions"]),
    kind: "COMMENT" as BlockCommentModelT["kind"],
    data: "",
    timestamp: new Date(payload.updatedAt).getTime(),
    position: payload.positions as [number, number, number, number],
  };
}
export function mapPayloadToBlockPreset(payload: BlockResponse<"presets">): BlockPresetModelT {
  const presetData: BlockPresetModelT["data"] =
    payload.preset === "PRIORITY_FAULT_EVENTS"
      ? {
          title: payload.title,
          preset: payload.preset,
          systemIds: payload.systemIds ?? [],
        }
      : {
          title: payload.title,
          preset: payload.preset,
        };
  return {
    id: payload.id,
    dashboardId: payload.dashboardId,
    kind: "PRESET" as BlockPresetModelT["kind"],
    data: {
      ...presetData,
    },
    timestamp: new Date(payload.updatedAt).getTime(),
    position: payload.positions as [number, number, number, number],
  };
}

export function mapPayloadToBlockHeading(payload: BlockResponse<"headings">): BlockHeadingModelT {
  return {
    ...omitObject(payload, ["createdAt", "updatedAt", "userName", "content", "level", "positions"]),
    kind: "HEADING" as BlockHeadingModelT["kind"],
    timestamp: new Date(payload.updatedAt).getTime(),
    data: payload.content,
    config: { level: payload.level },
    position: payload.positions as [number, number, number, number],
  };
}

export function mapPayloadToBlockImage(payload: BlockResponse<"images">): BlockImageModelT {
  return {
    ...omitObject(payload, ["createdAt", "updatedAt", "userName", "path", "title", "positions"]),
    kind: "IMAGE" as BlockImageModelT["kind"],
    timestamp: new Date(payload.updatedAt).getTime(),
    data: payload.title,
    config: { url: payload.path },
    position: payload.positions as [number, number, number, number],
  };
}

export function mapPayloadToBlockPlot(payload: BlockResponse<"plots">): BlockPlotModelT {
  return {
    ...omitObject(payload, [
      "createdAt",
      "updatedAt",
      "userName",
      "positions",
      "period",
      //   "resources",
      "options",
      "title",
      "layers",
    ]),
    kind: "PLOT" as BlockPlotModelT["kind"],
    timestamp: new Date(payload.updatedAt).getTime(),
    data: {
      title: payload.title ?? undefined,
      period: payload.period ?? undefined,
      transforms: payload.transforms.map(t => ({
        aggregate: t.aggregates,
        filter: t.filters.map(f => ({ ...f, value: transformFilterValues(f.value) })),
        groupBy: t.groupBy,
      })),
    },
    config: {
      options: {
        ...payload.options,
        displayInvalidatedDataPoints: payload.options?.displayInvalidatedDataPoints ?? false,
      },
      layers: payload.layers.map(layer => ({
        ...omitObject(layer, ["id"]),
        name: layer.name ?? undefined,
        position: (layer.position?.toLowerCase() ??
          undefined) as BlockPlotModelT["config"]["layers"][number]["position"],
        mark: layer.mark.toLowerCase() as BlockPlotModelT["config"]["layers"][number]["mark"],
        encoding: {
          x: { ...layer.encoding.x, quantity: layer.encoding.x.quantity ?? undefined },
          y: { ...layer.encoding.y, quantity: layer.encoding.y.quantity ?? undefined },
          color: layer.encoding.color as EncodingT,
        },
        zoom: layer.zoom,
        rule: layer.rule,
      })),
    },
    position: payload.positions as [number, number, number, number],
  };
}

export function mapPayloadToBlockDiagram(payload: BlockResponse<"diagrams">): BlockDiagramModelT {
  return {
    id: payload.id,
    dashboardId: payload.dashboardId,
    kind: "DIAGRAM",
    timestamp: new Date(payload.updatedAt).getTime(),
    data: {
      title: payload.title ?? undefined,
      period: payload.period ?? undefined,
      transforms: payload.transforms.map(t => ({
        aggregate: t.aggregates,
        filter: t.filters.map(f => ({ ...f, value: transformFilterValues(f.value) })),
        groupBy: t.groupBy,
      })),
    },
    config: {
      url: payload.url ?? "",
      live: payload.live ?? false,
      show_all: payload.showAll ?? true,
      show_role: payload.showRole ?? false,
      aggregate: payload.aggregate ?? false,
      viewport: payload.viewport ? { ...payload.viewport } : null,
      layers: payload.layers.map(layer => ({
        encodings: layer.values.map(v => ({
          identifier: v.identifier,
          column: v.column,
          quantity: v.quantity || undefined,
        })),
        name: layer.name,
        position: layer.positions,
        anchorRelativePositions: layer.anchorRelativePositions ?? [],
        anchor: {
          x: layer.anchor.x.toLowerCase() as "right" | "left" | "center",
          y: layer.anchor.y.toLowerCase() as "top" | "bottom" | "center",
        },
        thresholds: layer.thresholds,
        evolutions: layer.evolutions,
        showLabel: layer.showLabel,
        showDescription: layer.showDescription,
      })),
    },
    position: payload.positions as [number, number, number, number],
  };
}

function transformFilterValues(values: string[]) {
  const re = /^NaN|-?((\d*\.\d+|\d+)([Ee][+-]?\d+)?|Infinity)$/;
  return values.map(v => {
    if (v === "null") return null;
    if (v === "true" || v === "false") return Boolean(v);
    if (re.test(v)) {
      const n = Number(v);
      return Number.isNaN(n) ? v : n;
    }
    return v;
  });
}
